import { Controller } from "@hotwired/stimulus"
import { TurboBeforeFetchRequestEvent } from "@hotwired/turbo"

import { isTopmostElement } from "@/helpers/utils"
import { isMobile } from "@/helpers/window_size"

export default class ModalController extends Controller<HTMLDivElement> {
  static targets = ["container"]
  declare readonly containerTarget: HTMLDivElement

  static values = { shouldTrackHistory: { type: Boolean, default: true } }
  declare readonly shouldTrackHistoryValue: boolean

  static readonly FADE_IN_DURATION = 100 // See `--fade-in-duration`
  private static readonly KEYDOWN_EVENT = "keydown"
  private static readonly INITIAL_CLASS = "--initial"
  private static readonly NO_STYLE_CLASS = "no-style"
  private static readonly OPEN_CLASS = "--modal-open"
  private static readonly SMOOTH_SCROLL_CLASS = "scroll-smooth"

  private scrollYPosition = 0

  initialize(): void {
    this.popStateHandler = this.popStateHandler.bind(this)
    this.beforeCache = this.beforeCache.bind(this)
    this.preventTurboRequest = this.preventTurboRequest.bind(this)
  }

  connect(): void {
    if (this.isNavigationalModal) {
      window.addEventListener("popstate", this.popStateHandler)
      window.addEventListener("pagehide", this.beforeCache)
      window.addEventListener("turbo:before-fetch-request", this.preventTurboRequest)

      this.syncWithHistory()
    }
  }

  disconnect(): void {
    if (this.isNavigationalModal) {
      window.removeEventListener("popstate", this.popStateHandler)
      window.removeEventListener("pagehide", this.beforeCache)
      window.removeEventListener("turbo:before-fetch-request", this.preventTurboRequest)
    }
  }

  backgroundClose(e: Event): void {
    // Modal spans entire viewport when open, so checking background click means comparing
    // click target to modal itself.
    if (e.target === this.element) {
      this.close()
    }
  }

  close(e?: KeyboardEvent): void {
    // this ignores artifical keydown events, such as those
    // sent when using browser auto-fill
    const isArtificialKeydown = e?.type === ModalController.KEYDOWN_EVENT && e?.key === undefined
    const shouldClose = this.isOpen && !isArtificialKeydown

    if (!shouldClose) {
      return
    }

    this.performClose(true)

    if (this.shouldNavModalBeOpen) {
      // Remove history entry added on `open`. We use `history#back` because
      // with `history#replaceState` closing the modal and then swiping back does nothing.
      window.requestAnimationFrame(() => window.history.back())
    }
  }

  get containerId(): string {
    return this.containerTarget.id
  }

  open(event?: DispatchEvent<{ modalId?: string }>): void {
    const matchesModal = !event?.detail.modalId || event.detail.modalId === this.containerId
    const shouldOpen = !this.isOpen && matchesModal

    if (!shouldOpen) {
      return
    }

    if (this.isNavigationalModal) {
      const { history, location } = window
      const currentState = (history.state || {}) as Record<string, unknown>
      const urlWithHash = `${location.pathname}${location.search}#${this.containerId}`

      history.pushState({ ...currentState, previousHash: location.hash }, "", urlWithHash)
    }

    this.saveScrollPosition()
    this.performOpen(true)
  }

  useModalView(): void {
    this.element.classList.remove(ModalController.NO_STYLE_CLASS)
  }

  useNoStyleView(): void {
    document.body.classList.remove(ModalController.OPEN_CLASS)
    this.element.classList.add(ModalController.NO_STYLE_CLASS)
    this.element.removeAttribute("hidden")
  }

  private beforeCache(): void {
    this.setAnimationClass(false)
  }

  private syncWithHistory(): void {
    const { shouldNavModalBeOpen } = this

    if (shouldNavModalBeOpen !== this.isOpen) {
      if (shouldNavModalBeOpen) {
        this.performOpen(false)
      } else {
        this.performClose(false)
      }
    }
  }

  private focusInModal = (): void => {
    // To preserve focus within modal (e.g. from tabbing), check if the active element is outside
    // the modal and if so, re-focus the container.
    if (isTopmostElement(this.element) && !this.element.contains(document.activeElement)) {
      this.containerTarget.focus()
    }
  }

  private restoreScrollPosition(): void {
    // Turn off smooth scrolling
    document.documentElement.classList.remove(ModalController.SMOOTH_SCROLL_CLASS)

    // Restore position before modal opened
    window.scrollTo({ top: this.scrollYPosition })

    // Turn smooth scrolling back on
    window.requestAnimationFrame(() => {
      document.documentElement.classList.add(ModalController.SMOOTH_SCROLL_CLASS)
    })
  }

  private popStateHandler(): void {
    const { isOpen, shouldNavModalBeOpen } = this
    const shouldOpen = shouldNavModalBeOpen && !isOpen
    const shouldClose = !shouldNavModalBeOpen && isOpen

    if (shouldOpen) {
      window.requestAnimationFrame(() => this.performOpen(false))
    } else if (shouldClose) {
      this.performClose(false)
    }
  }

  // Even with Drive turned off Turbo still fires on `history.back()`, which we call in `close()`.
  // The only change in that call is to the URL's hash, so prevent request in that case.
  private preventTurboRequest = (e: TurboBeforeFetchRequestEvent): void => {
    const { pathname, search } = window.location
    const requestUrl = new URL(e.detail.url, window.location.href)
    const samePathAndSearch = pathname === requestUrl.pathname && search === requestUrl.search

    if (samePathAndSearch) {
      e.preventDefault()
    }
  }

  protected performOpen(shouldAnimate: boolean): void {
    this.setAnimationClass(shouldAnimate)
    document.body.classList.add(ModalController.OPEN_CLASS)
    document.addEventListener("focus", this.focusInModal, true)
    this.element.removeAttribute("hidden")

    // Focus modal on open (this also ensures `keydown` handler works without user interaction).
    // Elements with CSS `visibility: hidden` aren't focusable, so wait for animation.
    window.setTimeout(() => {
      this.containerTarget.focus()
    }, ModalController.FADE_IN_DURATION)

    this.dispatch("open")
  }

  protected performClose(shouldAnimate: boolean): void {
    if (!this.isOpen) {
      return
    }

    this.setAnimationClass(shouldAnimate)
    document.body.classList.remove(ModalController.OPEN_CLASS)
    document.removeEventListener("focus", this.focusInModal, true)
    this.element.setAttribute("hidden", "until-found")
    this.restoreScrollPosition()

    this.dispatch("closeModal")
  }

  private saveScrollPosition(): void {
    // CSS applies `position: fixed` when modal opens because iOS reasons. Doing so means we lose
    // page scroll position, so keep track of it to restore it on modal close.
    this.scrollYPosition = Math.max(0, window.scrollY)
    document.body.style.setProperty("--scroll-y-position", `${this.scrollYPosition}px`)
  }

  private setAnimationClass(shouldAnimate: boolean): void {
    this.element.classList.toggle(ModalController.INITIAL_CLASS, !shouldAnimate)
  }

  private get isNavigationalModal(): boolean {
    return isMobile() && this.shouldTrackHistoryValue
  }

  private get isOpen(): boolean {
    return !this.element.hasAttribute("hidden")
  }

  private get shouldNavModalBeOpen(): boolean {
    return this.isNavigationalModal && location.hash === `#${this.containerId}`
  }
}
